import { motion } from 'framer-motion';
import { AlertCircle, BarChart3, Clock, Crosshair, Target, TrendingUp, Zap } from 'lucide-react';
import React from 'react';
import MetricInfoTooltip from './MetricInfoTooltip';

interface TimeToGoLiveData {
  min: number;
  max: number;
  avg: number;
  median: number;
  percentile25: number;
  percentile75: number;
  percentile90: number;
  total: number;
  quartileCounts?: {
    q1: number; // 0-25%
    q2: number; // 25-50%
    q3: number; // 50-75%
    q4: number; // 75-100%
  };
}

interface TimeToGoLiveCardProps {
  data: TimeToGoLiveData;
  title: string;
  info?: string;
  query?: string;
}

export const TimeToGoLiveCard: React.FC<TimeToGoLiveCardProps> = ({ data, title, info, query }) => {
  const { min, max, avg, median, percentile25, percentile75, percentile90, total, quartileCounts } =
    data;

  return (
    <div className="w-full">
      {/* Title */}
      <div className="flex items-center gap-3 mb-8">
        <div className="p-2.5 bg-gradient-to-br from-purple-100 to-purple-200 dark:from-purple-900/30 dark:to-purple-800/30 rounded-xl">
          <Clock className="w-6 h-6 text-purple-600 dark:text-purple-400" />
        </div>
        <h3 className="text-xl font-bold text-gray-900 dark:text-white">{title}</h3>
        <MetricInfoTooltip info={info} query={query} />
      </div>

      {/* Key Metrics Cards - Top Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
        {/* Fastest Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, type: 'spring', stiffness: 120 }}
          whileHover={{ scale: 1.03, y: -5 }}
          className="group"
        >
          <div className="relative overflow-hidden bg-white dark:bg-gray-800 rounded-xl p-5 border-2 border-emerald-200 dark:border-emerald-800/30 shadow-sm hover:shadow-xl transition-all duration-300">
            {/* Subtle gradient accent at top */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-400 to-green-500" />

            {/* Decorative glow effect */}
            <div className="absolute -top-10 -right-10 w-24 h-24 bg-emerald-400/10 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-500" />

            <div className="relative">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2.5">
                  <div className="p-2.5 bg-emerald-50 dark:bg-emerald-900/30 rounded-xl border border-emerald-200 dark:border-emerald-800/50 shadow-sm">
                    <Zap
                      className="w-5 h-5 text-emerald-600 dark:text-emerald-400"
                      strokeWidth={2.5}
                    />
                  </div>
                  <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">
                    Fastest
                  </span>
                </div>
              </div>

              <div className="flex items-baseline gap-2 mb-2">
                <span className="text-5xl font-black text-emerald-600 dark:text-emerald-400 tracking-tight">
                  {Math.round(min)}
                </span>
                <span className="text-base font-semibold text-emerald-600/70 dark:text-emerald-400/70">
                  days
                </span>
              </div>

              <div className="flex items-center gap-1.5">
                <Zap className="w-3.5 h-3.5 text-emerald-500" strokeWidth={2.5} />
                <p className="text-xs font-medium text-gray-600 dark:text-gray-400">Record time</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Median Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, type: 'spring', stiffness: 120 }}
          whileHover={{ scale: 1.03, y: -5 }}
          className="group"
        >
          <div className="relative overflow-hidden bg-white dark:bg-gray-800 rounded-xl p-5 border-2 border-blue-200 dark:border-blue-800/30 shadow-sm hover:shadow-xl transition-all duration-300">
            {/* Subtle gradient accent at top */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-400 to-indigo-500" />

            {/* Decorative glow effect */}
            <div className="absolute -top-10 -right-10 w-24 h-24 bg-blue-400/10 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-500" />

            <div className="relative">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2.5">
                  <div className="p-2.5 bg-blue-50 dark:bg-blue-900/30 rounded-xl border border-blue-200 dark:border-blue-800/50 shadow-sm">
                    <Target
                      className="w-5 h-5 text-blue-600 dark:text-blue-400"
                      strokeWidth={2.5}
                    />
                  </div>
                  <span className="text-xs font-bold text-blue-600 dark:text-blue-400 uppercase tracking-wider">
                    Median
                  </span>
                </div>
              </div>

              <div className="flex items-baseline gap-2 mb-2">
                <span className="text-5xl font-black text-blue-600 dark:text-blue-400 tracking-tight">
                  {Math.round(median)}
                </span>
                <span className="text-base font-semibold text-blue-600/70 dark:text-blue-400/70">
                  days
                </span>
              </div>

              <div className="flex items-center gap-1.5">
                <Crosshair className="w-3.5 h-3.5 text-blue-500" strokeWidth={2.5} />
                <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
                  50th percentile
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Average Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, type: 'spring', stiffness: 120 }}
          whileHover={{ scale: 1.03, y: -5 }}
          className="group"
        >
          <div className="relative overflow-hidden bg-white dark:bg-gray-800 rounded-xl p-5 border-2 border-purple-200 dark:border-purple-800/30 shadow-sm hover:shadow-xl transition-all duration-300">
            {/* Subtle gradient accent at top */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-purple-400 to-violet-500" />

            {/* Decorative glow effect */}
            <div className="absolute -top-10 -right-10 w-24 h-24 bg-purple-400/10 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-500" />

            <div className="relative">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2.5">
                  <div className="p-2.5 bg-purple-50 dark:bg-purple-900/30 rounded-xl border border-purple-200 dark:border-purple-800/50 shadow-sm">
                    <TrendingUp
                      className="w-5 h-5 text-purple-600 dark:text-purple-400"
                      strokeWidth={2.5}
                    />
                  </div>
                  <span className="text-xs font-bold text-purple-600 dark:text-purple-400 uppercase tracking-wider">
                    Average
                  </span>
                </div>
              </div>

              <div className="flex items-baseline gap-2 mb-2">
                <span className="text-5xl font-black text-purple-600 dark:text-purple-400 tracking-tight">
                  {Math.round(avg)}
                </span>
                <span className="text-base font-semibold text-purple-600/70 dark:text-purple-400/70">
                  days
                </span>
              </div>

              <div className="flex items-center gap-1.5">
                <BarChart3 className="w-3.5 h-3.5 text-purple-500" strokeWidth={2.5} />
                <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
                  Mean duration
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Longest Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, type: 'spring', stiffness: 120 }}
          whileHover={{ scale: 1.03, y: -5 }}
          className="group"
        >
          <div className="relative overflow-hidden bg-white dark:bg-gray-800 rounded-xl p-5 border-2 border-orange-200 dark:border-orange-800/30 shadow-sm hover:shadow-xl transition-all duration-300">
            {/* Subtle gradient accent at top */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-orange-400 to-red-500" />

            {/* Decorative glow effect */}
            <div className="absolute -top-10 -right-10 w-24 h-24 bg-orange-400/10 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-500" />

            <div className="relative">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2.5">
                  <div className="p-2.5 bg-orange-50 dark:bg-orange-900/30 rounded-xl border border-orange-200 dark:border-orange-800/50 shadow-sm">
                    <AlertCircle
                      className="w-5 h-5 text-orange-600 dark:text-orange-400"
                      strokeWidth={2.5}
                    />
                  </div>
                  <span className="text-xs font-bold text-orange-600 dark:text-orange-400 uppercase tracking-wider">
                    Longest
                  </span>
                </div>
              </div>

              <div className="flex items-baseline gap-2 mb-2">
                <span className="text-5xl font-black text-orange-600 dark:text-orange-400 tracking-tight">
                  {Math.round(max)}
                </span>
                <span className="text-base font-semibold text-orange-600/70 dark:text-orange-400/70">
                  days
                </span>
              </div>

              <div className="flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5 text-orange-500" strokeWidth={2.5} />
                <p className="text-xs font-medium text-gray-600 dark:text-gray-400">Maximum time</p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Distribution Bar */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5, type: 'spring', stiffness: 80 }}
        className="mb-8"
      >
        <div className="bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 rounded-2xl p-6 border border-gray-200 dark:border-gray-700">
          <h4 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-4 uppercase tracking-wide">
            Distribution Timeline
          </h4>

          {/* Timeline Container */}
          <div className="relative">
            {/* Main gradient bar */}
            <div className="h-16 bg-gradient-to-r from-emerald-400 via-yellow-400 to-red-500 rounded-xl shadow-lg relative overflow-hidden">
              {/* Shine effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-pulse" />

              {/* Percentile markers */}
              <div className="absolute inset-0 flex items-center justify-between px-4">
                {/* 25th percentile */}
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.7, type: 'spring', stiffness: 200 }}
                  className="absolute"
                  style={{ left: '25%' }}
                >
                  <div className="flex flex-col items-center -translate-x-1/2">
                    <div className="w-3 h-3 bg-white rounded-full shadow-lg border-2 border-emerald-500" />
                  </div>
                </motion.div>

                {/* Median (50th percentile) */}
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.8, type: 'spring', stiffness: 200 }}
                  className="absolute"
                  style={{ left: '50%' }}
                >
                  <div className="flex flex-col items-center -translate-x-1/2">
                    <div className="w-4 h-4 bg-white rounded-full shadow-lg border-2 border-blue-500" />
                  </div>
                </motion.div>

                {/* 75th percentile */}
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.9, type: 'spring', stiffness: 200 }}
                  className="absolute"
                  style={{ left: '75%' }}
                >
                  <div className="flex flex-col items-center -translate-x-1/2">
                    <div className="w-3 h-3 bg-white rounded-full shadow-lg border-2 border-orange-500" />
                  </div>
                </motion.div>
              </div>
            </div>

            {/* Labels below the bar */}
            <div className="flex justify-between mt-3 text-xs font-semibold">
              <div className="text-emerald-700 dark:text-emerald-400">
                <div>{Math.round(min)}d</div>
                <div className="text-[10px] text-gray-500 dark:text-gray-400">Start</div>
              </div>
              <div className="text-emerald-600 dark:text-emerald-400 -ml-4">
                <div>{Math.round(percentile25)}d</div>
                <div className="text-[10px] text-gray-500 dark:text-gray-400">25%</div>
              </div>
              <div className="text-blue-600 dark:text-blue-400">
                <div>{Math.round(median)}d</div>
                <div className="text-[10px] text-gray-500 dark:text-gray-400">50%</div>
              </div>
              <div className="text-orange-600 dark:text-orange-400 -mr-4">
                <div>{Math.round(percentile75)}d</div>
                <div className="text-[10px] text-gray-500 dark:text-gray-400">75%</div>
              </div>
              <div className="text-red-700 dark:text-red-400">
                <div>{Math.round(max)}d</div>
                <div className="text-[10px] text-gray-500 dark:text-gray-400">Max</div>
              </div>
            </div>

            {/* Quartile Clinic Counts - Below labels */}
            {quartileCounts && (
              <div className="mt-6 grid grid-cols-4 gap-4">
                {/* Q1: 0-25% - Fastest */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 1.0, type: 'spring', stiffness: 120 }}
                  whileHover={{ scale: 1.03, y: -3 }}
                  className="group"
                >
                  <div className="relative overflow-hidden bg-white dark:bg-gray-800 rounded-xl p-4 border-2 border-emerald-200 dark:border-emerald-800/30 shadow-sm hover:shadow-lg transition-all duration-300">
                    {/* Subtle gradient accent at top */}
                    <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-400 to-green-500" />

                    <div className="text-center">
                      <div className="text-3xl font-black text-emerald-600 dark:text-emerald-400 mb-2">
                        {quartileCounts.q1}
                      </div>
                      <div className="text-xs font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider mb-1">
                        Fastest 25%
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                        ≤ {Math.round(percentile25)}d
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* Q2: 25-50% */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 1.1, type: 'spring', stiffness: 120 }}
                  whileHover={{ scale: 1.03, y: -3 }}
                  className="group"
                >
                  <div className="relative overflow-hidden bg-white dark:bg-gray-800 rounded-xl p-4 border-2 border-green-200 dark:border-green-800/30 shadow-sm hover:shadow-lg transition-all duration-300">
                    {/* Subtle gradient accent at top */}
                    <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-green-400 to-lime-500" />

                    <div className="text-center">
                      <div className="text-3xl font-black text-green-600 dark:text-green-400 mb-2">
                        {quartileCounts.q2}
                      </div>
                      <div className="text-xs font-bold text-green-600 dark:text-green-400 uppercase tracking-wider mb-1">
                        25-50%
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                        {Math.round(percentile25)}-{Math.round(median)}d
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* Q3: 50-75% */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 1.2, type: 'spring', stiffness: 120 }}
                  whileHover={{ scale: 1.03, y: -3 }}
                  className="group"
                >
                  <div className="relative overflow-hidden bg-white dark:bg-gray-800 rounded-xl p-4 border-2 border-yellow-200 dark:border-yellow-800/30 shadow-sm hover:shadow-lg transition-all duration-300">
                    {/* Subtle gradient accent at top */}
                    <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-yellow-400 to-orange-400" />

                    <div className="text-center">
                      <div className="text-3xl font-black text-yellow-600 dark:text-yellow-400 mb-2">
                        {quartileCounts.q3}
                      </div>
                      <div className="text-xs font-bold text-yellow-600 dark:text-yellow-400 uppercase tracking-wider mb-1">
                        50-75%
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                        {Math.round(median)}-{Math.round(percentile75)}d
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* Q4: 75-100% - Slowest */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 1.3, type: 'spring', stiffness: 120 }}
                  whileHover={{ scale: 1.03, y: -3 }}
                  className="group"
                >
                  <div className="relative overflow-hidden bg-white dark:bg-gray-800 rounded-xl p-4 border-2 border-orange-200 dark:border-orange-800/30 shadow-sm hover:shadow-lg transition-all duration-300">
                    {/* Subtle gradient accent at top */}
                    <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-orange-400 to-red-500" />

                    <div className="text-center">
                      <div className="text-3xl font-black text-orange-600 dark:text-orange-400 mb-2">
                        {quartileCounts.q4}
                      </div>
                      <div className="text-xs font-bold text-orange-600 dark:text-orange-400 uppercase tracking-wider mb-1">
                        Slowest 25%
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                        &gt; {Math.round(percentile75)}d
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            )}
          </div>
        </div>
      </motion.div>

      {/* Quick Stats Grid */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6, type: 'spring', stiffness: 100 }}
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {/* Stat 1 - Middle 50% */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.7, type: 'spring', stiffness: 120 }}
            whileHover={{ scale: 1.02, y: -3 }}
          >
            <div className="relative overflow-hidden bg-white dark:bg-gray-800 rounded-xl p-5 border-2 border-blue-200 dark:border-blue-800/30 shadow-sm hover:shadow-lg transition-all duration-300">
              {/* Subtle gradient accent at top */}
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-400 to-indigo-500" />

              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-50 dark:bg-blue-900/30 rounded-lg border border-blue-200 dark:border-blue-800/50">
                  <div className="w-2.5 h-2.5 bg-blue-500 rounded-full animate-pulse" />
                </div>
                <div className="flex-1">
                  <div className="text-xs font-bold text-blue-600 dark:text-blue-400 uppercase tracking-wider mb-1">
                    Middle 50%
                  </div>
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Half of all clinics complete between{' '}
                    <span className="font-black text-blue-600 dark:text-blue-400 text-base whitespace-nowrap">
                      {Math.round(percentile25)}-{Math.round(percentile75)} days
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Stat 2 - Top Performers */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.8, type: 'spring', stiffness: 120 }}
            whileHover={{ scale: 1.02, y: -3 }}
          >
            <div className="relative overflow-hidden bg-white dark:bg-gray-800 rounded-xl p-5 border-2 border-purple-200 dark:border-purple-800/30 shadow-sm hover:shadow-lg transition-all duration-300">
              {/* Subtle gradient accent at top */}
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-purple-400 to-violet-500" />

              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-50 dark:bg-purple-900/30 rounded-lg border border-purple-200 dark:border-purple-800/50">
                  <div className="w-2.5 h-2.5 bg-purple-500 rounded-full animate-pulse" />
                </div>
                <div className="flex-1">
                  <div className="text-xs font-bold text-purple-600 dark:text-purple-400 uppercase tracking-wider mb-1">
                    Top Performers
                  </div>
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    25% of clinics go live in under{' '}
                    <span className="font-black text-purple-600 dark:text-purple-400 text-base whitespace-nowrap">
                      {Math.round(percentile25)} days
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Stat 3 - Sample Size */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.9, type: 'spring', stiffness: 120 }}
            whileHover={{ scale: 1.02, y: -3 }}
          >
            <div className="relative overflow-hidden bg-white dark:bg-gray-800 rounded-xl p-5 border-2 border-emerald-200 dark:border-emerald-800/30 shadow-sm hover:shadow-lg transition-all duration-300">
              {/* Subtle gradient accent at top */}
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-400 to-green-500" />

              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-50 dark:bg-emerald-900/30 rounded-lg border border-emerald-200 dark:border-emerald-800/50">
                  <div className="w-2.5 h-2.5 bg-emerald-500 rounded-full animate-pulse" />
                </div>
                <div className="flex-1">
                  <div className="text-xs font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider mb-1">
                    Sample Size
                  </div>
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Analysis based on{' '}
                    <span className="font-black text-emerald-600 dark:text-emerald-400 text-base whitespace-nowrap">
                      {total} {total === 1 ? 'clinic' : 'clinics'}
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};
